/**
 * ============================================================================
 * 🎯 NAME FORMATTING UTILITIES
 * ============================================================================
 * 
 * Single source of truth for formatting person names (staff, coaches, etc.)
 * Requirements Summary: UI copy should say coach while preserving instructor
 * schema naming, and shared name formatting can hide coach gender when needed.
 * 
 * DO NOT:
 * - Create inline name formatting logic in components
 * - Duplicate name formatting patterns across files
 * 
 * ALWAYS:
 * - Import name formatting functions from this file
 * - Use consistent formatting patterns across the application
 * 
 * @module nameUtils
 */

import { GenderUtils, GENDER_LABELS } from '@/utils/constants/genderConstants';

/**
 * Instructor data structure for formatting
 */
export interface InstructorData {
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  gender?: string | null;
  preferred_name?: string | null;
}

/**
 * Format instructor name as "First Name L." with optional gender
 * 
 * Formats instructor name as:
 * - "First Name L." (first name + initial last name)
 * - "First Name L. (Gender)" if gender is specified and not empty
 * - Uses preferred_name if available
 * - Falls back to display_name if first/last name not available
 * 
 * Gender is only displayed if:
 * - Gender value exists
 * - Gender is not empty string
 * - Gender is not "prefer_not_to_say"
 * 
 * @param instructor - Instructor data object
 * @returns Formatted name string or null if no name data available
 * 
 * @example
 * formatInstructorDisplay({ first_name: "John", last_name: "Doe", gender: "male" })
 * // Returns: "John D. (Male)"
 * 
 * @example
 * formatInstructorDisplay({ first_name: "Jane", last_name: "Smith" })
 * // Returns: "Jane S."
 * 
 * @example
 * formatInstructorDisplay({ preferred_name: "Coach Mike", gender: "male" })
 * // Returns: "Coach Mike (Male)"
 * 
 * @example
 * formatInstructorDisplay({ first_name: "John", last_name: "Doe", gender: "" })
 * // Returns: "John D." (gender hidden because empty)
 */
export function formatInstructorDisplay(
  instructor: InstructorData | null | undefined,
  options: { showGender?: boolean } = {}
): string | null {
  if (!instructor) return null;
  
  let displayName: string | null = null;
  
  // Use preferred_name if available
  if (instructor.preferred_name) {
    displayName = instructor.preferred_name;
  }
  // Otherwise use first name + initial last name
  else if (instructor.first_name && instructor.last_name) {
    const lastNameInitial = instructor.last_name.charAt(0).toUpperCase();
    displayName = `${instructor.first_name} ${lastNameInitial}.`;
  }
  // Fallback to display_name if available
  else if (instructor.display_name) {
    displayName = instructor.display_name;
  }
  
  if (!displayName) return null;
  
  // Add gender only if specified (not empty/null/prefer_not_to_say)
  const showGender = options.showGender ?? true;
  const gender = showGender &&
                 instructor.gender &&
                 instructor.gender !== '' &&
                 instructor.gender !== 'prefer_not_to_say'
    ? ` (${GenderUtils.getGenderLabel(instructor.gender)})` 
    : '';
  
  return `${displayName}${gender}`;
}

/**
 * Matches the trailing " (Male)" / " (Prefer not to say)" suffix that the
 * instructor formatters above append. Built from GENDER_LABELS so the known
 * options stay in sync.
 *
 * This is NOT airtight on its own: `auth_users.gender` is free text with no CHECK
 * constraint, and `GenderUtils.getGenderLabel()` title-cases any unrecognized
 * value, so a row with gender 'nonbinary' formats as "Jane D. (Nonbinary)" and
 * slips past. `getCoachInitials` therefore drops parenthesized tokens outright
 * rather than trusting this pattern.
 */
const GENDER_SUFFIX_PATTERN = new RegExp(
  `\\s*\\((?:${[...Object.values(GENDER_LABELS), 'Not specified']
    .map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})\\)\\s*$`,
  'i'
);

/**
 * Remove the trailing gender label from an already-formatted display name.
 *
 * @example stripGenderSuffix('Jane D. (Female)')  // 'Jane D.'
 * @example stripGenderSuffix('Jane D.')           // 'Jane D.'
 */
export function stripGenderSuffix(name: string | null | undefined): string {
  if (!name?.trim()) return '';
  return name.trim().replace(GENDER_SUFFIX_PATTERN, '').trim();
}

/**
 * Initials for a coach avatar fallback, derived from an already-formatted
 * display name (the output of `formatInstructorDisplay` and friends).
 *
 * Two cleanups happen before initials are picked, and both are load-bearing:
 *
 * 1. Parenthesized tokens are dropped. These names carry a trailing gender label,
 *    and taking the first letter of the last token otherwise yields the garbled
 *    "J(" that shipped to families. Dropping by shape rather than by matching
 *    GENDER_SUFFIX_PATTERN keeps this correct for gender values the pattern
 *    does not know about.
 * 2. A leading "Coach " title is dropped, so preferred names like "Coach Mike"
 *    read as the person. It is KEPT when the following token is not a letter —
 *    "Coach 01 Smith" would otherwise render as "0S", and a digit in the avatar
 *    circle reads as a glitch to a parent.
 *
 * @example getCoachInitials('Jane D. (Female)')     // 'JD'
 * @example getCoachInitials('Jane D. (Nonbinary)')  // 'JD'
 * @example getCoachInitials('Coach Mike')           // 'MI'
 * @example getCoachInitials('Coach 01 S.')          // 'CS'
 * @example getCoachInitials(null)                   // 'C'
 */
export function getCoachInitials(name: string | null | undefined): string {
  const parts = stripGenderSuffix(name)
    .split(/\s+/)
    .filter((part) => part && !part.startsWith('('));

  const titled = parts.length > 1 && /^coach$/i.test(parts[0]);
  const named = titled && /^\p{L}/u.test(parts[1]) ? parts.slice(1) : parts;

  if (named.length === 0) return 'C';
  if (named.length === 1) return named[0].slice(0, 2).toUpperCase();
  return `${named[0][0]}${named[named.length - 1][0]}`.toUpperCase();
}

/**
 * Format instructor name for combobox/dropdown labels with optional gender
 * 
 * Formats instructor name as:
 * - "First Name Last Name" (full name)
 * - "First Name Last Name (Gender)" if gender is specified and not empty
 * 
 * Gender is only displayed if:
 * - Gender value exists
 * - Gender is not empty string
 * - Gender is not "prefer_not_to_say"
 * 
 * @param instructor - Instructor data object with auth_user
 * @returns Formatted name string
 * 
 * @example
 * formatInstructorComboboxLabel({ 
 *   auth_user: { first_name: "John", last_name: "Doe", gender: "male" } 
 * })
 * // Returns: "John Doe (Male)"
 * 
 * @example
 * formatInstructorComboboxLabel({ 
 *   auth_user: { first_name: "Jane", last_name: "Smith" } 
 * })
 * // Returns: "Jane Smith"
 */
export function formatInstructorComboboxLabel(instructor: {
  auth_user?: {
    first_name?: string | null;
    last_name?: string | null;
    gender?: string | null;
  } | null;
}): string {
  if (!instructor?.auth_user) {
    return 'Unknown Coach';
  }
  
  const { first_name, last_name, gender } = instructor.auth_user;
  const fullName = `${first_name || ''} ${last_name || ''}`.trim();
  
  if (!fullName) {
    return 'Unknown Coach';
  }
  
  // Add gender only if specified (not empty/null/prefer_not_to_say)
  const genderLabel = gender && 
                      gender !== '' && 
                      gender !== 'prefer_not_to_say'
    ? ` (${GenderUtils.getGenderLabel(gender)})` 
    : '';
  
  return `${fullName}${genderLabel}`;
}

/**
 * Extract first name from a full name string
 * 
 * Handles both "John" and "John Doe" formats by splitting on the first space.
 * Used primarily for email template variables where only the first name is needed.
 * 
 * IMPORTANT: This function is used for email template variables like {{athlete_first_name}}
 * which must be ONLY the first name (e.g., "John"), NOT the full name (e.g., "John Doe").
 * This ensures professional email subject lines and consistent template usage.
 * 
 * @param fullName - Full name string (e.g., "John Doe" or just "John")
 * @returns First name only (e.g., "John")
 * 
 * @example
 * extractFirstName("John Doe")
 * // Returns: "John"
 * 
 * @example
 * extractFirstName("John")
 * // Returns: "John"
 * 
 * @example
 * extractFirstName("  John Michael Doe  ")
 * // Returns: "John" (trims whitespace first)
 */
export function extractFirstName(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return ''; // Return empty string for whitespace-only input
  return trimmed.split(' ')[0] || '';
}

/**
 * Format a person's full name from separate first/last parts.
 *
 * Each part is trimmed individually, then the non-empty parts are joined with a
 * single space. Returns an empty string when both parts are blank/missing —
 * callers that want a nullable result should use `formatFullName(a, b) || null`.
 *
 * This is the canonical "first + last" formatter. It is deliberately distinct
 * from the instructor formatters above (which abbreviate the last name and can
 * append gender) — use this for plain athlete/parent display names.
 *
 * @example formatFullName('John', 'Doe')   // 'John Doe'
 * @example formatFullName('  Jane ', '')    // 'Jane'
 * @example formatFullName(null, null)       // ''
 */
export function formatFullName(
  first?: string | null,
  last?: string | null
): string {
  const f = typeof first === 'string' ? first.trim() : '';
  const l = typeof last === 'string' ? last.trim() : '';
  return [f, l].filter(Boolean).join(' ').trim();
}
